import { useEffect, useState } from 'react'
import { Container } from '../../components/layout/Container'
import { Card } from '../../components/ui/Card'
import { knowledgeApi } from '../../lib/api'
import { FileText, FolderOpen, ChevronRight } from 'lucide-react'
import { MarkdownRenderer } from '../../components/knowledge/MarkdownRenderer'
import { cn } from '../../lib/utils'
import type { KnowledgeDocument } from '../../types'

interface TreeNode {
  id: string
  name: string
  type: 'folder' | 'file'
  children?: TreeNode[]
}

function buildTree(documents: KnowledgeDocument[]): TreeNode[] {
  const root: TreeNode = { id: 'root', name: 'knowledge_base', type: 'folder', children: [] }

  documents.forEach((doc) => {
    const parts = doc.localFilePath.split('/')
    let current = root

    parts.forEach((part, index) => {
      const isLast = index === parts.length - 1
      const existing = current.children?.find((c) => c.name === part)

      if (existing) {
        current = existing
      } else {
        const newNode: TreeNode = {
          id: isLast ? doc.id : `${part}-${index}`,
          name: part,
          type: isLast ? 'file' : 'folder',
          children: isLast ? undefined : [],
        }
        current.children?.push(newNode)
        if (!isLast) current = newNode
      }
    })
  })

  return root.children ?? []
}

export function VaultPage() {
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([])
  const [selected, setSelected] = useState<KnowledgeDocument | null>(null)
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    knowledgeApi
      .getDocuments()
      .then((docs) => {
        setDocuments(docs)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const handleSelect = async (doc: KnowledgeDocument) => {
    setSelected(doc)
    setContent(null)
    try {
      const full = await knowledgeApi.getDocument(doc.id)
      setContent(full.content)
    } catch {
      setContent('Error al cargar el contenido.')
    }
  }

  const tree = buildTree(documents)

  const renderNode = (node: TreeNode, depth = 0) => {
    const isFile = node.type === 'file'
    const doc = isFile ? documents.find((d) => d.id === node.id) : null

    return (
      <div key={node.id} style={{ paddingLeft: `${depth * 16}px` }}>
        <button
          type="button"
          onClick={() => doc && handleSelect(doc)}
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
            isFile
              ? 'text-text-secondary hover:bg-surface hover:text-text-primary'
              : 'text-text-muted'
          )}
        >
          {isFile ? (
            <FileText className="h-4 w-4 text-accent-light" />
          ) : (
            <FolderOpen className="h-4 w-4 text-text-muted" />
          )}
          <span className="truncate">{node.name}</span>
          {isFile && <ChevronRight className="ml-auto h-3.5 w-3.5 text-text-muted" />}
        </button>
        {!isFile && node.children?.map((child) => renderNode(child, depth + 1))}
      </div>
    )
  }

  return (
    <Container size="wide" className="h-[calc(100vh-4rem)] py-6 lg:h-screen">
      <div className="grid h-full gap-6 lg:grid-cols-4">
        <Card variant="elevated" className="flex h-full flex-col overflow-hidden lg:col-span-1">
          <div className="border-b border-border p-4">
            <h2 className="text-sm font-semibold text-text-primary">Bóveda Local (.md)</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent" />
              </div>
            ) : (
              tree.map((node) => renderNode(node))
            )}
          </div>
        </Card>

        <Card variant="elevated" className="flex h-full flex-col overflow-hidden lg:col-span-3">
          {selected ? (
            <>
              <div className="border-b border-border p-4">
                <div className="flex items-center gap-2 text-sm font-mono text-text-muted">
                  <FileText className="h-4 w-4" />
                  {selected.localFilePath}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {content == null ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent" />
                  </div>
                ) : (
                  <article className="prose prose-invert max-w-none">
                    <MarkdownRenderer content={content} />
                  </article>
                )}
              </div>
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center text-text-muted">
              <FileText className="mb-3 h-12 w-12" />
              <p>Selecciona un documento para ver su contenido</p>
            </div>
          )}
        </Card>
      </div>
    </Container>
  )
}
